import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { ReactNode } from 'react';

import {
  ContentPosition,
  getLandingImageSrc,
  hexToRgba,
  LandingConfig,
  TextAlign,
} from './landing-config';

interface LandingPreviewProps {
  c: LandingConfig;
  /** When provided, the primary CTA becomes interactive (e.g. real login). */
  onPrimaryClick?: () => void;
  onSecondaryClick?: () => void;
  primaryLoading?: boolean;
  primaryStartIcon?: ReactNode;
}

const ALIGNMENT_MAP: Record<TextAlign, string> = {
  center: 'center',
  right: 'flex-end',
  left: 'flex-start',
};

const getBoxSx = (c: LandingConfig) => {
  if (!c.boxEnabled) return {};
  return {
    bgcolor: hexToRgba(c.boxColor, c.boxOpacity / 100),
    backdropFilter:
      c.boxEffect === 'glass' ? `blur(${c.boxBlur}px)` : undefined,
    boxShadow:
      c.boxEffect === 'shadow'
        ? `0 ${c.boxShadow}px ${c.boxShadow * 2}px rgba(0,0,0,0.18)`
        : undefined,
    backgroundImage:
      c.boxEffect === 'gradient'
        ? `linear-gradient(135deg, ${hexToRgba(
            c.boxColor,
            0.95
          )}, ${hexToRgba(c.primaryBtnColor, 0.25)})`
        : undefined,
    borderRadius: `${c.borderRadius}px`,
  };
};

/**
 * The configured padding on desktop; phones cap it so a roomy desktop setting
 * doesn't eat a 320px screen.
 */
const responsivePadding = (px: number) => ({
  xs: `${Math.min(px, 24)}px`,
  md: `${px}px`,
});

const getOverlayJustify = (position: ContentPosition): string => {
  if (position === 'left') return 'flex-start';
  if (position === 'right') return 'flex-end';
  return 'center';
};

const getOverlayAlign = (position: ContentPosition): string => {
  if (position === 'top') return 'flex-start';
  if (position === 'bottom') return 'flex-end';
  return 'center';
};

interface PreviewContentProps {
  c: LandingConfig;
  flexAlignment: string;
  overlay: boolean;
  onPrimaryClick?: () => void;
  onSecondaryClick?: () => void;
  primaryLoading: boolean;
  primaryStartIcon?: ReactNode;
}

const PreviewContent = ({
  /** Fills its parent's height; the parent decides how tall the hero gets. */
  c,
  flexAlignment,
  overlay,
  onPrimaryClick,
  onSecondaryClick,
  primaryLoading,
  primaryStartIcon,
}: PreviewContentProps) => {
  /**
   * Over the hero image the secondary CTA needs a solid fill to stay readable;
   * on the plain background it sits flush. Held in one place because the hover
   * rule has to repeat it: MUI's outlined `:hover` tint out-specifies the flat
   * class `bgcolor` generates, so leaving hover unset let the fill flip to a
   * translucent blue on pointer-over.
   */
  const secondaryBg = overlay ? '#fff' : 'transparent';

  return (
    <Stack
      spacing={1.75}
      sx={{
        textAlign: c.textAlign,
        alignItems: flexAlignment,
        maxWidth: c.contentWidth,
        width: '100%',
        mx: overlay ? undefined : 'auto',
      }}
    >
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 600,
          color: c.captionColor,
        }}
      >
        {c.caption}
      </Typography>
      <Typography
        sx={{
          fontSize: { xs: 28, sm: 34, md: 40 },
          lineHeight: 1.15,
          overflowWrap: 'anywhere',
          fontWeight: 800,
          color: c.headlineColor,
        }}
      >
        {c.headline}
      </Typography>
      <Typography
        sx={{
          fontSize: { xs: 15, md: 17 },
          lineHeight: 1.6,
          color: c.paragraphColor,
        }}
      >
        {c.paragraph}
      </Typography>
      <Stack
        direction='row'
        spacing={2}
        useFlexGap
        flexWrap='wrap'
        justifyContent={flexAlignment}
        sx={{ pt: 1 }}
      >
        {c.primaryEnabled && (
          <Button
            disableElevation
            variant='contained'
            onClick={onPrimaryClick}
            disabled={primaryLoading}
            startIcon={
              primaryLoading ? (
                <CircularProgress size='1rem' color='inherit' />
              ) : (
                primaryStartIcon
              )
            }
            sx={{
              bgcolor: c.primaryBtnColor,
              color: '#fff',
              textTransform: 'none',
              fontSize: 16,
              px: 3,
              py: 1.1,
              borderRadius: 1.5,
              cursor: onPrimaryClick ? 'pointer' : 'default',
              '&:hover': { bgcolor: c.primaryBtnColor, opacity: 0.9 },
            }}
          >
            {c.primaryLabel}
          </Button>
        )}
        {c.secondaryEnabled && (
          <Button
            variant='outlined'
            onClick={onSecondaryClick}
            sx={{
              color: c.secondaryBtnColor,
              borderColor: c.secondaryBtnColor,
              bgcolor: secondaryBg,
              textTransform: 'none',
              fontSize: 16,
              px: 3,
              py: 1.1,
              borderRadius: 1.5,
              borderWidth: 2,
              cursor: onSecondaryClick ? 'pointer' : 'default',
              '&:hover': {
                bgcolor: secondaryBg,
                borderColor: c.secondaryBtnColor,
                borderWidth: 2,
              },
            }}
          >
            {c.secondaryLabel}
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

const LandingPreview = ({
  c,
  onPrimaryClick,
  onSecondaryClick,
  primaryLoading = false,
  primaryStartIcon,
}: LandingPreviewProps) => {
  const imageSrc = getLandingImageSrc(c);
  const isOverlay =
    c.imagePosition === 'full' || c.imagePosition === 'background';
  const flexAlignment = ALIGNMENT_MAP[c.textAlign];
  const boxSx = getBoxSx(c);

  const img = (
    <Box
      component='img'
      src={imageSrc}
      alt='preview'
      sx={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
      }}
    />
  );

  /* ---- Overlay layouts (full width / background) ---- */
  if (isOverlay) {
    const justify = getOverlayJustify(c.contentPosition);
    const align = getOverlayAlign(c.contentPosition);

    return (
      <Box
        sx={{
          position: 'relative',
          borderRadius: `${c.borderRadius}px`,
          overflow: 'hidden',
          // Phones: grow with the copy (the page scrolls) instead of
          // squeezing it into a fixed-height hero.
          height: { xs: 'auto', md: '100%' },
          minHeight: { xs: 420, md: 0 },
          backgroundImage: `url(${imageSrc})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          justifyContent: justify,
          alignItems: align,
          p: responsivePadding(c.contentPadding),
        }}
      >
        {c.imageOverlay && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: hexToRgba(
                c.imageOverlayColor,
                c.imageOverlayOpacity / 100
              ),
            }}
          />
        )}
        <Box
          sx={{
            position: 'relative',
            maxWidth: '100%',
            ...boxSx,
            p: c.boxEnabled ? { xs: 2.5, md: 4 } : 0,
          }}
        >
          <PreviewContent
            c={c}
            flexAlignment={flexAlignment}
            overlay={true}
            onPrimaryClick={onPrimaryClick}
            onSecondaryClick={onSecondaryClick}
            primaryLoading={primaryLoading}
            primaryStartIcon={primaryStartIcon}
          />
        </Box>
      </Box>
    );
  }

  /* ---- Split layouts (image left / right) ---- */
  if (c.imagePosition === 'left' || c.imagePosition === 'right') {
    const imageFirst = c.imagePosition === 'left';
    const imageBlock = (
      <Box
        key='img'
        sx={{
          // Phones stack: image on top at a fixed band height, copy below.
          flex: { xs: 'none', md: '1 1 0' },
          height: { xs: 220, sm: 280, md: 'auto' },
          order: { xs: -1, md: 0 },
          minWidth: 0,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {img}
      </Box>
    );
    const contentBlock = (
      <Box
        key='content'
        sx={{
          flex: { xs: 'none', md: '1 1 0' },
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: responsivePadding(c.contentPadding),
          // Very short desktop viewports scroll the copy rather than the
          // whole page; phones let the page scroll the stacked layout.
          minHeight: 0,
          overflowY: { xs: 'visible', md: 'auto' },
        }}
      >
        <Box
          sx={{
            width: '100%',
            ...boxSx,
            p: c.boxEnabled ? { xs: 2.5, md: 4 } : 0,
          }}
        >
          <PreviewContent
            c={c}
            flexAlignment={flexAlignment}
            overlay={false}
            onPrimaryClick={onPrimaryClick}
            onSecondaryClick={onSecondaryClick}
            primaryLoading={primaryLoading}
            primaryStartIcon={primaryStartIcon}
          />
        </Box>
      </Box>
    );
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          bgcolor: '#ffffff',
          borderRadius: `${c.borderRadius}px`,
          overflow: 'hidden',
          height: { xs: 'auto', md: '100%' },
        }}
      >
        {imageFirst ? [imageBlock, contentBlock] : [contentBlock, imageBlock]}
      </Box>
    );
  }

  return null;
};

export default LandingPreview;
